import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import each from 'lodash/each';
import keys from 'lodash/keys';
import size from 'lodash/size';
import intersection from 'lodash/intersection';
import PropTypes from 'prop-types';
import { makeStyles, Button, Divider } from '@material-ui/core';

import MultiSelectMenu from '../MultiSelectMenu';
import { getI18n } from "../../../utils/intl";
import FormTextField from '../../FormTextField';
import styles from './styles';

const SHOW_ALL = 'SHOW_ALL';
const SHOW_SELECTED = 'SHOW_SELECTED';

const FilterByNameAPIPerOrg = (props) => {
  const {
    primaryPlaceHolderText, secondaryPlaceHolderText, selectedItems = {}, hideCount,
    maxAllowedEntities, showSecondaryText, filterData, updateSelectedItems,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    primarySearchText: '',
    secondarySearchText: '',
    invalidSelection: false,
    visilbilityFilter: SHOW_ALL,
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  const onVisibilityFilterToggle = () => {
    const visilbilityFilter =
      state.visilbilityFilter === SHOW_ALL ? SHOW_SELECTED : SHOW_ALL;
    setState({
      visilbilityFilter,
    });
  };

  const getMenuItem = (val, key, showSecondaryText) => {
    if (showSecondaryText) {
      const { name, secondaryName } = val;
      const secondaryTooltip = (<span>{secondaryName}</span>);
      return ({ name, secondaryName, label: name, value: key, secondaryTooltip });
    }
    return ({ name: val, label: val, value: key });
  };

  const getMenuData = () => {
    const menuData = [];
    each(filterData, (val, key) => {
      const primarySearchText = state.primarySearchText.trim();
      const secondarySearchText = state.secondarySearchText.trim();
      if (state.visilbilityFilter === SHOW_SELECTED) {
        if (selectedItems[key]) {
          if (shouldIncludeItem(val, primarySearchText, secondarySearchText)) {
            menuData.push(getMenuItem(val, key, showSecondaryText));
          }
        }
      } else if (shouldIncludeItem(val, primarySearchText, secondarySearchText)) {
        menuData.push(getMenuItem(val, key, showSecondaryText));
      }
    });
    return menuData;
  };

  const shouldIncludeItem = (item, primarySearchText, secondarySearchText) => {
    const { name, secondaryName } = item;
    const primarySearchTextLC = primarySearchText.toLowerCase();
    const secondarySearchTextLC = secondarySearchText.toLowerCase();
    const nameLC = name && name.toLowerCase();
    const secondaryNameLC = secondaryName.toLowerCase();
    if (((primarySearchTextLC.length < 3) || (nameLC.includes(primarySearchTextLC))) &&
    ((secondarySearchTextLC.length < 3) || (secondaryNameLC.includes(secondarySearchTextLC)))) {
      return true;
    }
    return false;
  };

  const handlePrimarySearch = value => {
    setState({
      primarySearchText: value,
    });
  };

  const handleSecondarySearch = value => {
    setState({
      secondarySearchText: value,
    });
  };

  const handleOnSelect = (value, checked) => {
    if (checked) {
      selectedItems[value] = true;
    } else if (selectedItems[value]) {
      delete selectedItems[value];
    }
    setState({ invalidSelection: size(selectedItems) > maxAllowedEntities });
    let selectedItemsIds = keys(selectedItems);
    const allIds = keys(filterData);
    selectedItemsIds = intersection(selectedItemsIds, allIds);
    const updatedSelectedItems = {};
    each(selectedItemsIds, item => { updatedSelectedItems[item] = true; });
    updateSelectedItems(updatedSelectedItems);
  };

  const defaultPrimaryPlaceHolderText = intl.getI18nMessage('label.analyticsDashboard.filter.dialog.search.placeholder.api');
  const defaultSecondaryPlaceHolderText = intl.getI18nMessage('label.analyticsDashboard.filter.dialog.search.placeholder.org');
  const selectedEntities = keys(selectedItems).length;
  const remainingSlots =
    maxAllowedEntities - selectedEntities < 0
      ? maxAllowedEntities
      : maxAllowedEntities - selectedEntities;
  const menuData = getMenuData();

  return (
    <div className={classes.filterContainer}>
      <div className={classes.searchBar}>
        <FormTextField
          fieldContainerClass={classes.searchFieldContainerClass}
          handleChange={handlePrimarySearch}
          placeholder={primaryPlaceHolderText || defaultPrimaryPlaceHolderText}
          value={state.primarySearchText}
        />
      </div>
      <div className={classes.searchBar}>
        <FormTextField
          fieldContainerClass={classes.searchFieldContainerClass}
          handleChange={handleSecondarySearch}
          placeholder={secondaryPlaceHolderText || defaultSecondaryPlaceHolderText}
          value={state.secondarySearchText}
        />
      </div>
      <div className={classes.linkBar}>
        <Button
          className={classes.linkBtn}
          onClick={onVisibilityFilterToggle}
        >
          {state.visilbilityFilter === SHOW_ALL ? intl.getI18nMessage(
            'label.analyticsDashboard.multiSelect.label.showSelected',
          ) : intl.getI18nMessage(
            'label.analyticsDashboard.multiSelect.label.showAll',
          )}
        </Button>
      </div>
      <Divider className={classes.menuDivider} />
      <MultiSelectMenu
        data={menuData}
        selectedData={selectedItems}
        handleOnSelect={handleOnSelect}
        showSecondaryText={showSecondaryText}
      />
      <Divider className={classes.menuDivider} />
      {hideCount ? null : (
        <div className={classes.filterFooter}>
          <span className={classes.slotsDetail}>
            {remainingSlots}/{maxAllowedEntities} data slots remaining
          </span>
          {state.invalidSelection ? (
            <p className={classes.errorMessage}>
              {intl.getI18nMessage('label.analytics.filter.filterByName.errorMessage.text')}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

FilterByNameAPIPerOrg.propTypes = {
  filterData: PropTypes.object,
  selectedItems: PropTypes.object,
  primaryPlaceHolderText: PropTypes.string,
  secondaryPlaceHolderText: PropTypes.string,
  updateSelectedItems: PropTypes.func,
  maxAllowedEntities: PropTypes.number,
  hideCount: PropTypes.bool,
  showSecondaryText: PropTypes.bool,
};

export default FilterByNameAPIPerOrg;
